import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { motion } from 'framer-motion';
import { Download, Calendar, SlidersHorizontal, X, FileSpreadsheet, Image } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import * as htmlToImage from 'html-to-image';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import CommonLoader from '../../components/CommonLoader';
import ReportDateTabs from '../../components/reports/ReportDateTabs';
import useReportDateRange, { REPORT_TABS, rangeLengthInDays, parsePeriod } from '../../hooks/useReportDateRange';
import { FilterPanel, FilterSection, FilterOptionList, FilterCountryList, FilterPopover } from '../../components/common/FilterPanel';
import { getTenantCount, downloadTenantCountExcel, getPlatformReportFilters } from '../../redux/platformReportsSlice';
import { unlessSystemId } from '../../utils/systemId';

// TENANT COUNT — how many tenants were created per day (or per month, once
// the range is longer than two months), stacked by edition. Same shape as the
// Patient Count page; reads GET /api/platform/reports/tenant-count
// (reports.view). Preset tabs use CALENDAR periods (this week / this month) so
// the buckets line up with the axis, as the Patient Count page does.

const EMPTY_FILTERS = { tenantType: '', countryId: '' };
// Categorical series colours, in fixed order (validated for colour-vision
// separation against the light surface — do not reorder or cycle).
const TYPE_COLORS = ['#22A2A2', '#7E5FB5', '#D08A3C', '#C4567A', '#5A9E4F'];
const MAX_DAY_BUCKETS = 62;

const fmtNum = (n) => new Intl.NumberFormat('en-IN').format(Number(n) || 0);
const fmtPeriod = (period, groupBy) => {
    const d = parsePeriod(period);
    if (!d) return String(period ?? '');
    return groupBy === 'month'
        ? d.toLocaleDateString('en-GB', { month: 'short', year: 'numeric' })
        : d.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit' });
};

const ReportsTenantCount = () => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const { tenantCount, tenantCountSummary, tenantCountTypes, tenantCountLoading, tenantCountDownloadLoading, filters: filterOptions } = useSelector((state) => state.platformReports);

    const range = useReportDateRange({ defaultTab: 'month', calendar: true });
    const [isFilterOpen, setIsFilterOpen] = useState(false);
    const [tempFilters, setTempFilters] = useState(EMPTY_FILTERS);
    const [filters, setFilters] = useState(EMPTY_FILTERS);
    const [isDownloadMenuOpen, setIsDownloadMenuOpen] = useState(false);
    const [groupBy, setGroupBy] = useState('day');
    const filterButtonRef = useRef(null);
    const filterRef = useRef(null);
    const downloadButtonRef = useRef(null);
    const downloadMenuRef = useRef(null);
    const chartRef = useRef(null);

    useEffect(() => {
        dispatch(getPlatformReportFilters());
    }, [dispatch]);

    // The endpoint's own `types` list names and orders the series; the
    // /filters list is the fallback (and always drives the filter popover).
    const tenantTypes = useMemo(
        () => (tenantCountTypes?.length ? tenantCountTypes : (filterOptions?.tenantTypes || [])),
        [tenantCountTypes, filterOptions?.tenantTypes]
    );
    const countries = useMemo(
        () => (filterOptions?.countries || []).map((c) => ({ countryId: c.countryId, countryName: c.name })),
        [filterOptions?.countries]
    );
    const typeLabel = (value) => tenantTypes.find((x) => x.value === value)?.label || String(unlessSystemId(value, '')).replace(/[_-]+/g, ' ');
    const countryLabel = (id) => countries.find((c) => c.countryId === id)?.countryName || '—';

    const buildParams = (r) => {
        const gb = rangeLengthInDays(r.from, r.to) > MAX_DAY_BUCKETS ? 'month' : 'day';
        return { ...r, groupBy: gb, tenantType: filters.tenantType, countryId: filters.countryId };
    };

    const fetchCountData = async () => {
        const r = range.resolveRange();
        if (!r) return;
        const params = buildParams(r);
        setGroupBy(params.groupBy);
        const result = await dispatch(getTenantCount(params));
        if (getTenantCount.rejected.match(result)) {
            toast.error(result.payload?.error || t('reportsTenantCount.load_failed', 'Failed to load the tenant count.'));
        }
    };

    useEffect(() => {
        fetchCountData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [range.activeTab, range.startDate, range.endDate, filters]);

    useEffect(() => {
        if (isFilterOpen) setTempFilters({ ...filters });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isFilterOpen]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (
                filterRef.current && !filterRef.current.contains(event.target) &&
                filterButtonRef.current && !filterButtonRef.current.contains(event.target)
            ) {
                setTempFilters({ ...filters });
                setIsFilterOpen(false);
            }
            if (
                downloadMenuRef.current && !downloadMenuRef.current.contains(event.target) &&
                downloadButtonRef.current && !downloadButtonRef.current.contains(event.target)
            ) {
                setIsDownloadMenuOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [filters]);

    // Series = every tenant type that appears in the rows or the summary, in
    // the catalogue's order (so a type keeps its colour whatever the filter).
    const rows = useMemo(() => (range.ready ? tenantCount : []), [range.ready, tenantCount]);
    const summary = useMemo(() => (range.ready ? tenantCountSummary : { total: 0, byType: {} }), [range.ready, tenantCountSummary]);
    const seriesKeys = useMemo(() => {
        const seen = new Set();
        rows.forEach((row) => Object.keys(row.byType || {}).forEach((k) => seen.add(k)));
        Object.keys(summary?.byType || {}).forEach((k) => seen.add(k));
        const ordered = tenantTypes.map((tt) => tt.value).filter((v) => seen.has(v));
        const rest = [...seen].filter((v) => !ordered.includes(v)).sort();
        return [...ordered, ...rest];
    }, [rows, summary, tenantTypes]);
    const colorOf = (key) => TYPE_COLORS[Math.max(0, seriesKeys.indexOf(key)) % TYPE_COLORS.length];

    const chartData = rows.map((row) => {
        const point = { label: fmtPeriod(row.period, groupBy), total: Number(row.count) || 0 };
        seriesKeys.forEach((key) => { point[key] = Number(row.byType?.[key]) || 0; });
        return point;
    });

    const downloadBlob = (blob, filename) => {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
    };

    const handleDownloadExcel = async () => {
        const r = range.resolveRange({ warn: true });
        if (!r) return;
        setIsDownloadMenuOpen(false);
        try {
            const resultAction = await dispatch(downloadTenantCountExcel(buildParams(r)));
            if (downloadTenantCountExcel.fulfilled.match(resultAction)) {
                downloadBlob(resultAction.payload, `tenant-count-${r.from}-to-${r.to}.xlsx`);
                toast.success(t('reportsTenantCount.download_success', 'Downloaded successfully.'));
            } else {
                toast.warning(t('reportsTenantCount.download_error', 'Failed to download.'));
            }
        } catch {
            toast.warning(t('reportsTenantCount.download_error', 'Failed to download.'));
        }
    };

    const handleDownloadPng = async () => {
        const r = range.resolveRange({ warn: true });
        if (!r) return;
        if (!chartRef.current) {
            toast.warning(t('reportsTenantCount.chart_not_found', 'Chart not found.'));
            return;
        }
        setIsDownloadMenuOpen(false);
        try {
            const dataUrl = await htmlToImage.toPng(chartRef.current, { backgroundColor: '#ffffff', pixelRatio: 2, cacheBust: true });
            const link = document.createElement('a');
            link.href = dataUrl;
            link.download = `tenant-count-${r.from}-to-${r.to}.png`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            toast.success(t('reportsTenantCount.chart_download_success', 'Chart downloaded successfully.'));
        } catch (error) {
            console.error('Error downloading chart:', error);
            toast.warning(t('reportsTenantCount.chart_download_error', 'Failed to download chart.'));
        }
    };

    const hasActiveFilters = Boolean(filters.tenantType || filters.countryId);

    return (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.4 }}
            className="bg-white w-full rounded-2xl p-4 sm:p-6 shadow-md"
            data-testid="reports-tenant-count-container"
        >
            {/* Header */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4" data-testid="reports-tenant-count-header">
                <div>
                    <h3 className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] bg-clip-text text-transparent flex items-center gap-3" data-testid="reports-tenant-count-title">
                        {t('reportsTenantCount.title', 'Tenant Count')}
                    </h3>
                    <p className="mt-1 text-xs text-gray-400" data-testid="reports-tenant-count-subtitle">
                        {t('reportsTenantCount.platform_wide', 'Tenants created on the platform, by edition')}
                    </p>
                </div>

                <div className="relative" data-testid="reports-tenant-count-download-container">
                    <motion.button
                        ref={downloadButtonRef}
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        onClick={() => setIsDownloadMenuOpen(!isDownloadMenuOpen)}
                        disabled={tenantCountDownloadLoading || tenantCountLoading || chartData.length === 0}
                        className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-gradient-to-r from-[#4ABDBD] to-[#3A9B9B] rounded-lg shadow-sm hover:shadow-md transition-all disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
                        data-testid="reports-tenant-count-download-button"
                    >
                        <Download className="w-4 h-4" />
                        {t('reportsTenantCount.download', 'Download')}
                    </motion.button>

                    {isDownloadMenuOpen && (
                        <motion.div
                            ref={downloadMenuRef}
                            initial={{ opacity: 0, y: -10 }}
                            animate={{ opacity: 1, y: 0 }}
                            className="absolute z-20 top-full right-0 mt-2 bg-white shadow-2xl rounded-xl border border-gray-200 w-48 overflow-hidden"
                            data-testid="reports-tenant-count-download-menu"
                        >
                            <button
                                type="button"
                                onClick={handleDownloadExcel}
                                disabled={tenantCountDownloadLoading}
                                className="w-full flex items-center gap-3 px-4 py-3 text-sm font-medium text-gray-700 hover:bg-gradient-to-r hover:from-blue-50 hover:to-cyan-50 transition-all cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                                data-testid="reports-tenant-count-download-excel-button"
                            >
                                <FileSpreadsheet className="w-5 h-5 text-green-600" />
                                <span>{tenantCountDownloadLoading ? t('reportsTenantCount.downloading', 'Downloading...') : t('reportsTenantCount.excel', 'Excel')}</span>
                            </button>
                            <div className="border-t border-gray-100"></div>
                            <button
                                type="button"
                                onClick={handleDownloadPng}
                                className="w-full flex items-center gap-3 px-4 py-3 text-sm font-medium text-gray-700 hover:bg-gradient-to-r hover:from-blue-50 hover:to-cyan-50 transition-all cursor-pointer"
                                data-testid="reports-tenant-count-download-png-button"
                            >
                                <Image className="w-5 h-5 text-blue-600" />
                                <span>{t('reportsTenantCount.png', 'PNG')}</span>
                            </button>
                        </motion.div>
                    )}
                </div>
            </div>

            {/* Tabs & filter */}
            <div className="mb-6">
                <ReportDateTabs range={range} tabs={REPORT_TABS} layoutId="activeTenantCountTab" testIdPrefix="reports-tenant-count">
                    <div className="relative w-full sm:w-auto" data-testid="reports-tenant-count-filter-container">
                        <button
                            ref={filterButtonRef}
                            type="button"
                            onClick={() => setIsFilterOpen(!isFilterOpen)}
                            className="group flex items-center justify-center gap-2 w-full sm:w-auto h-full px-3 py-2.5 text-white bg-gradient-to-br from-[#4ABDBD] to-[#3A9B9B] rounded-lg shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300 font-semibold text-sm cursor-pointer"
                            aria-label={t('reportsCommon.filter', 'Filter')}
                            aria-expanded={isFilterOpen}
                            data-testid="reports-tenant-count-filter-button"
                        >
                            <SlidersHorizontal className="w-5 h-5" />
                        </button>

                        {isFilterOpen && (
                            <FilterPopover ref={filterRef}>
                                <FilterPanel
                                    onClear={() => { setTempFilters(EMPTY_FILTERS); setFilters(EMPTY_FILTERS); setIsFilterOpen(false); }}
                                    onApply={() => { setFilters(tempFilters); setIsFilterOpen(false); }}
                                    clearLabel={t('reportsCommon.filter_clear', 'Clear')}
                                    applyLabel={t('reportsCommon.filter_apply', 'Apply')}
                                >
                                    <FilterSection title={t('reportsTenantCount.filter_tenant_type', 'Tenant type')} isActive={Boolean(tempFilters.tenantType)}>
                                        <FilterOptionList value={tempFilters.tenantType} onChange={(val) => setTempFilters({ ...tempFilters, tenantType: val })} options={filterOptions?.tenantTypes || []} />
                                    </FilterSection>
                                    <FilterSection title={t('reportsTenantCount.filter_country', 'Country')} isActive={Boolean(tempFilters.countryId)}>
                                        <FilterCountryList
                                            value={tempFilters.countryId}
                                            onChange={(val) => setTempFilters({ ...tempFilters, countryId: val })}
                                            countries={countries}
                                            searchPlaceholder={t('reportsCommon.search_placeholder', 'Search...')}
                                        />
                                    </FilterSection>
                                </FilterPanel>
                            </FilterPopover>
                        )}
                    </div>
                    <span className="text-xs text-gray-400 sm:ml-2" data-testid="reports-tenant-count-group-by">
                        {groupBy === 'month' ? t('reportsTenantCount.per_month', 'Per month') : t('reportsTenantCount.per_day', 'Per day')}
                    </span>
                </ReportDateTabs>
            </div>

            {hasActiveFilters && (
                <div className="mb-6 bg-gradient-to-r from-blue-50/80 to-cyan-50/80 rounded-xl p-4 border border-gray-200 shadow-sm" data-testid="reports-tenant-count-applied-filters-container">
                    <div className="flex flex-wrap items-center gap-2">
                        <span className="text-sm font-semibold text-gray-700 mr-2">{t('reportsCommon.applied_filters', 'Applied Filters')}:</span>
                        {filters.tenantType && (
                            <div className="inline-flex items-center gap-2 bg-white px-3 py-1.5 rounded-lg border border-gray-300 shadow-sm">
                                <span className="text-xs text-gray-600"><span className="font-semibold">{t('reportsTenantCount.filter_tenant_type', 'Tenant type')}:</span> {typeLabel(filters.tenantType)}</span>
                                <button type="button" onClick={() => setFilters({ ...filters, tenantType: '' })} className="text-gray-400 hover:text-red-500 cursor-pointer"><X className="h-3.5 w-3.5" /></button>
                            </div>
                        )}
                        {filters.countryId && (
                            <div className="inline-flex items-center gap-2 bg-white px-3 py-1.5 rounded-lg border border-gray-300 shadow-sm">
                                <span className="text-xs text-gray-600"><span className="font-semibold">{t('reportsTenantCount.filter_country', 'Country')}:</span> {countryLabel(filters.countryId)}</span>
                                <button type="button" onClick={() => setFilters({ ...filters, countryId: '' })} className="text-gray-400 hover:text-red-500 cursor-pointer"><X className="h-3.5 w-3.5" /></button>
                            </div>
                        )}
                        <button
                            type="button"
                            onClick={() => { setFilters(EMPTY_FILTERS); setTempFilters(EMPTY_FILTERS); }}
                            className="ml-auto inline-flex items-center gap-1.5 bg-gradient-to-r from-red-500 to-red-600 text-white px-3 py-1.5 rounded-lg text-xs font-semibold hover:from-red-600 hover:to-red-700 transition-all shadow-sm cursor-pointer"
                            data-testid="reports-tenant-count-clear-all-button"
                        >
                            <X className="h-3.5 w-3.5" />
                            {t('reportsCommon.clear_all', 'Clear All')}
                        </button>
                    </div>
                </div>
            )}

            {tenantCountLoading && (
                <div className="flex flex-col items-center justify-center py-4 pt-12" data-testid="reports-tenant-count-loading-container">
                    <CommonLoader />
                    <span className="text-xl text-[#4ABDBD]">{t('reportsCommon.loading', 'Loading...')}</span>
                </div>
            )}

            {!tenantCountLoading && (
                <>
                    {/* Summary chips */}
                    <div className="flex flex-wrap items-center gap-2 mb-4" data-testid="reports-tenant-count-summary">
                        <span className="inline-flex items-center gap-1.5 text-xs font-bold border rounded-full px-3 py-1 bg-[#E7F5F4] text-[#2D8A8A] border-[#4ABDBD]" data-testid="reports-tenant-count-summary-total">
                            {t('reportsTenantCount.total', 'Total')}: {fmtNum(summary?.total)}
                        </span>
                        {seriesKeys.map((key) => (
                            <span key={key} className="inline-flex items-center gap-1.5 text-xs font-semibold border border-gray-200 rounded-full px-3 py-1 bg-white text-gray-700" data-testid={`reports-tenant-count-summary-${key}`}>
                                <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: colorOf(key) }} />
                                {typeLabel(key)}: {fmtNum(summary?.byType?.[key])}
                            </span>
                        ))}
                    </div>

                    <div ref={chartRef} className="bg-white p-4 rounded-lg" data-testid="reports-tenant-count-chart-container">
                        {chartData.length > 0 ? (
                            <ResponsiveContainer width="100%" height={380}>
                                <BarChart data={chartData} margin={{ top: 20, right: 30, left: 0, bottom: 0 }}>
                                    <CartesianGrid strokeDasharray="4 4" stroke="#e0e0e0" opacity={0.5} vertical={false} />
                                    <XAxis dataKey="label" tick={{ fontSize: 12, fill: '#666' }} height={50} />
                                    <YAxis tick={{ fontSize: 13, fill: '#666' }} allowDecimals={false} />
                                    <Tooltip
                                        cursor={{ fill: 'rgba(74, 189, 189, 0.1)' }}
                                        contentStyle={{
                                            backgroundColor: 'white',
                                            border: '1px solid #e5e7eb',
                                            borderRadius: '12px',
                                            boxShadow: '0 10px 25px rgba(0,0,0,0.1)',
                                            padding: '12px',
                                            fontSize: '13px',
                                        }}
                                        labelStyle={{ fontWeight: 'bold', color: '#4ABDBD', marginBottom: '8px' }}
                                        formatter={(value, name) => [fmtNum(value), typeLabel(name)]}
                                    />
                                    {seriesKeys.length > 1 && <Legend formatter={(value) => <span className="text-xs text-gray-600">{typeLabel(value)}</span>} />}
                                    {seriesKeys.map((key, i) => (
                                        <Bar
                                            key={key}
                                            dataKey={key}
                                            name={key}
                                            stackId="tenants"
                                            fill={colorOf(key)}
                                            stroke="#ffffff"
                                            strokeWidth={1}
                                            radius={i === seriesKeys.length - 1 ? [6, 6, 0, 0] : 0}
                                            barSize={22}
                                        />
                                    ))}
                                </BarChart>
                            </ResponsiveContainer>
                        ) : (
                            <div className="flex flex-col items-center justify-center h-96 text-gray-400" data-testid="reports-tenant-count-no-data-container">
                                <Calendar className="w-16 h-16 mb-4 text-gray-300" />
                                <p className="text-lg font-medium text-gray-500">{t('reportsCommon.no_data', 'No data available.')}</p>
                                <p className="text-sm text-gray-400 mt-1">{t('reportsTenantCount.no_data_subtitle', 'Try adjusting your filters.')}</p>
                            </div>
                        )}
                    </div>
                </>
            )}
        </motion.div>
    );
};

export default ReportsTenantCount;
